/* Isolated D22 draft storefront. Public reads on load; cart writes only after a customer's exact Add action. */
(() => {
  'use strict';
  const PAGE_SIZE = 24;
  const MAX_FEED_PAGES = 30;
  const FEED_LIMIT = 250;
  const CACHE_MS = 120000;
  const collator = new Intl.Collator('en-GB', { sensitivity: 'base', numeric: true });
  let dataRequest;
  let publicRequest;
  let modalSequence = 0;

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  function money(value, cents = false) {
    const currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active || 'GBP';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(cents ? value / 100 : value);
  }
  function localeRoot(root) {
    const configured = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || root.dataset.routesRoot || '/';
    try {
      const url = new URL(configured, window.location.origin);
      return url.origin === window.location.origin ? url.pathname.replace(/\/?$/, '/') : '/';
    } catch (_) { return '/'; }
  }
  async function getJSON(url, timeout = 20000) {
    const aborter = new AbortController();
    const timer = window.setTimeout(() => aborter.abort(), timeout);
    try {
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: aborter.signal });
      if (!response.ok) throw new Error('Current information is unavailable (' + response.status + ').');
      return await response.json();
    } finally { window.clearTimeout(timer); }
  }
  function imageURL(source, width = 560) {
    if (source && typeof source === 'object') source = source.src || source.url;
    if (!source) return null;
    try {
      const url = new URL(source, window.location.origin);
      if (url.protocol !== 'https:' && url.origin !== window.location.origin) return null;
      url.searchParams.set('width', String(width));
      return url.href;
    } catch (_) { return null; }
  }
  function compactProduct(product) {
    if (!product || !product.id || !product.handle || typeof product.title !== 'string' || !Array.isArray(product.variants)) return null;
    const options = (product.options || []).map((option, index) => typeof option === 'string' ? { name: option, position: index + 1, values: [] } : { name: option.name, position: option.position || index + 1, values: option.values || [] });
    const variants = product.variants.map(variant => ({
      id: String(variant.id), available: variant.available === true, price: Number(variant.price),
      options: Array.isArray(variant.options) ? variant.options : options.map((_, index) => variant['option' + (index + 1)])
    }));
    return { id: String(product.id), handle: product.handle, title: product.title, image: imageURL((product.images || [])[0] || product.image), options, variants };
  }
  async function publicProducts(root, data) {
    if (publicRequest) return publicRequest;
    const cacheKey = 'ii-k23-public-' + String(data.version || '1') + '-' + window.location.host;
    publicRequest = (async () => {
      try {
        const cached = JSON.parse(window.sessionStorage.getItem(cacheKey));
        if (cached && Date.now() - cached.at >= 0 && Date.now() - cached.at < CACHE_MS && cached.complete === true && Array.isArray(cached.products)) return { products: cached.products, pages: cached.pages, cached: true };
      } catch (_) { /* Storage may be unavailable or full; current public reads remain supported. */ }
      const products = [];
      const seen = new Set();
      let complete = false;
      let pages = 0;
      for (let page = 1; page <= MAX_FEED_PAGES; page += 1) {
        const url = new URL(localeRoot(root) + 'products.json', window.location.origin);
        url.searchParams.set('limit', String(FEED_LIMIT));
        url.searchParams.set('page', String(page));
        const payload = await getJSON(url.href);
        pages += 1;
        if (!payload || !Array.isArray(payload.products)) throw new Error('The public product feed could not be verified.');
        for (const raw of payload.products) {
          const product = compactProduct(raw);
          if (product && !seen.has(product.id)) { seen.add(product.id); products.push(product); }
        }
        if (payload.products.length < FEED_LIMIT) { complete = true; break; }
      }
      if (!complete) throw new Error('The public catalogue exceeded the verified pagination limit.');
      try { window.sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), complete, pages, products })); } catch (_) { /* Safe uncached fallback. */ }
      return { products, pages, cached: false };
    })().catch(error => { publicRequest = null; throw error; });
    return publicRequest;
  }
  function loadData(url) {
    if (!dataRequest) dataRequest = getJSON(url).catch(error => { dataRequest = null; throw error; });
    return dataRequest;
  }
  function clearPublicCache(data) {
    publicRequest = null;
    if (!data) return;
    try { window.sessionStorage.removeItem('ii-k23-public-' + String(data.version || '1') + '-' + window.location.host); } catch (_) { /* Optional cache only. */ }
  }

  function initialise(root) {
    if (root.dataset.k23Initialised === 'true') return;
    root.dataset.k23Initialised = 'true';
    const key = root.dataset.routeKey;
    const presentation = root.dataset.presentation;
    const tools = [...document.querySelectorAll('[data-k23-tools]')].filter(node => node.dataset.routeKey === key);
    const allControls = [root, ...tools];
    const nativeWrappers = presentation === 'catalogue-only' ? [...document.querySelectorAll('[id*="__d22native_"]')] : [];
    const nativeDisplay = new Map(nativeWrappers.map(wrapper => [wrapper, { value: wrapper.style.getPropertyValue('display'), priority: wrapper.style.getPropertyPriority('display') }]));
    const catalogue = root.querySelector('[data-k23-catalogue]');
    const explore = root.querySelector('[data-k23-explore]');
    const grid = root.querySelector('[data-k23-grid]');
    const status = root.querySelector('[data-k23-status]');
    const search = root.querySelector('[data-k23-search]');
    const sort = root.querySelector('[data-k23-sort]');
    const available = root.querySelector('[data-k23-available]');
    const priceMin = root.querySelector('[data-k23-price-min]');
    const priceMax = root.querySelector('[data-k23-price-max]');
    const facets = root.querySelector('[data-k23-facets]');
    const groupsNode = root.querySelector('[data-k23-groups]');
    const more = root.querySelector('[data-k23-more]');
    const errorNode = root.querySelector('[data-k23-error]');
    const errorText = root.querySelector('[data-k23-error-text]');
    const fallback = root.querySelector('[data-k23-fallback]');
    const notice = root.querySelector('[data-k23-cart-notice]');
    if (!grid || !status) return;
    let data;
    let route;
    let routes = new Map();
    let products = [];
    let productMap = new Map();
    let metadata = new Map();
    let loaded = false;
    let loading = false;
    let visibleCount = PAGE_SIZE;
    let selectedGroup = 'all';
    let searchTimer;
    let activeDialog = null;
    let verificationToken = 0;
    const optionFilters = new Map();
    const groupMembers = new Map();
    const pageStatus = { ready: false, key, expectedCount: 0, loadedCount: 0, errors: [], mode: 'explore' };
    window.II_K23_PAGE_STATUS = pageStatus;

    function signal() {
      window.II_K23_PAGE_STATUS = pageStatus;
      root.dataset.k23Ready = String(pageStatus.ready);
      document.dispatchEvent(new CustomEvent('ii-k23:page-status', { detail: { ...pageStatus } }));
    }
    function previewPath(path) {
      try {
        const url = new URL(path, window.location.origin);
        if (url.origin !== window.location.origin) return url.href;
        url.searchParams.delete('preview_theme_id');
        return url.pathname + url.search + url.hash;
      } catch (_) { return path; }
    }
    function routeURL(target) {
      if (!target) return '#';
      const path = target.previewUrl || target.url || target.canonicalPath || target.nativePath;
      const url = new URL(path, window.location.origin);
      if (target.view) url.searchParams.set('view', target.view);
      return previewPath(url.href);
    }
    function pdpURL(product, variantId) {
      const url = new URL(localeRoot(root) + 'products/' + encodeURIComponent(product.handle), window.location.origin);
      if (variantId) url.searchParams.set('variant', String(variantId));
      return previewPath(url.href);
    }
    function sceneURL(source, size) {
      if (!source) return null;
      if (/^https?:\/\//.test(source) || source.startsWith('//')) return new URL(source, window.location.origin).href;
      const name = size && /\.webp$/i.test(source) ? source.replace(/\.webp$/i, '-' + size + '.webp') : source;
      const base = new URL(root.dataset.assetsBase || '/', window.location.href);
      const url = new URL(name, base.href);
      url.searchParams.set('v', '20261022-vivid-3');
      return url.href;
    }
    function sceneImage(target, lazy = true) {
      const image = el('img');
      const source = target.sceneAsset;
      if (!source) return null;
      const atlas = /ii-k23-(?:men-hero|scene-men-studio)(?:-\d+)?\.webp$/i.test(source);
      image.src = sceneURL(source);
      if (/\.webp$/i.test(source) && !/^https?:/.test(source)) {
        image.srcset = sceneURL(source, 640) + ' 640w, ' + sceneURL(source, 960) + ' 960w, ' + sceneURL(source) + (atlas ? ' 1254w' : ' 1536w');
        image.sizes = '(max-width: 800px) 100vw, 50vw';
      }
      image.alt = target.sceneAlt || '';
      image.width = 1536;
      image.height = 1024;
      image.loading = lazy ? 'lazy' : 'eager';
      image.decoding = 'async';
      if (atlas) {
        image.classList.add('ii-k23-page__image-atlas'); image.width = 1254; image.height = 1254;
      }
      return image;
    }
    function normalise(fact, index) {
      const meta = metadata.get(fact.id) || {};
      const prices = fact.variants.map(variant => variant.price).filter(value => Number.isFinite(value) && value >= 0);
      return { ...fact, meta, available: fact.variants.some(variant => variant.available), minPrice: prices.length ? Math.min(...prices) : null, maxPrice: prices.length ? Math.max(...prices) : null, order: index, search: fact.title.toLocaleLowerCase('en-GB') };
    }
    function card(product) {
      const article = el('article', 'ii-k23-page__card');
      article.dataset.k23ProductId = product.id;
      const url = pdpURL(product, product.variants.length === 1 ? product.variants[0].id : null);
      const imageLink = el('a', 'ii-k23-page__card-image');
      imageLink.href = url;
      imageLink.setAttribute('aria-label', 'View ' + product.title);
      if (product.image) {
        const image = el('img'); image.src = product.image; image.alt = ''; image.width = 560; image.height = 650; image.loading = 'lazy'; image.decoding = 'async'; imageLink.append(image);
      } else imageLink.append(el('span', null, 'Image unavailable'));
      article.append(imageLink);
      const body = el('div', 'ii-k23-page__card-body');
      const heading = el('h3', 'ii-k23-page__card-title');
      const titleLink = el('a', null, product.title); titleLink.href = url; heading.append(titleLink); body.append(heading);
      body.append(el('p', 'ii-k23-page__card-price', product.minPrice === null ? 'See product details' : (product.maxPrice > product.minPrice ? 'From ' : '') + money(product.minPrice)));
      const optionNames = product.options.filter(option => option.name !== 'Title' && option.values.length > 1).map(option => option.name);
      if (optionNames.length) body.append(el('p', 'ii-k23-page__card-note', optionNames.join(' / ') + ' options'));
      if (!product.available) body.append(el('p', 'ii-k23-page__card-note ii-k23-page__card-note--unavailable', 'Sold out'));
      const actions = el('div', 'ii-k23-page__card-actions');
      const link = el('a', 'ii-k23-page__card-link', product.meta.forcePdp ? 'View details & options' : 'View details'); link.href = url; actions.append(link);
      const count = Number(product.meta.variantCount);
      if (product.available && !product.meta.forcePdp && Number.isInteger(count) && count > 0 && count <= 250) {
        const button = el('button', 'ii-k23-page__card-add', count === 1 ? 'Add to basket' : 'Choose options');
        button.type = 'button'; button.dataset.k23Quick = product.id; button.setAttribute('aria-label', (count === 1 ? 'Add to basket: ' : 'Choose options: ') + product.title); actions.append(button);
      }
      body.append(actions); article.append(body); return article;
    }

    function decorate() {
      const department = Array.isArray(data.departments) ? data.departments.find(item => item.key === route.dept) : data.departments && (data.departments[route.dept] || Object.values(data.departments).find(item => item.key === route.dept));
      root.dataset.dept = route.dept || root.dataset.dept;
      root.dataset.kind = route.kind || root.dataset.kind;
      const palette = getComputedStyle(root);
      tools.forEach(tool => {
        for (const name of ['--ink', '--deep', '--wash', '--paper', '--line', '--muted']) tool.style.setProperty(name, palette.getPropertyValue(name));
        tool.querySelectorAll('[data-k23-mode="catalogue"]').forEach(button => { if (catalogue && catalogue.id) button.setAttribute('aria-controls', catalogue.id); });
      });
      root.querySelectorAll('[data-k23-eyebrow], [data-k23-hero-eyebrow]').forEach(node => { node.textContent = department && department.eyebrow || 'The Islamic Impressions collection'; });
      const nativeLink = root.querySelector('[data-k23-native-link]');
      if (nativeLink) nativeLink.href = route.fallbackUrl || nativeLink.href;
      const breadcrumbs = root.querySelector('[data-k23-breadcrumbs]');
      if (breadcrumbs) {
        const home = el('a', null, 'Home'); home.href = localeRoot(root);
        const fragment = document.createDocumentFragment(); fragment.append(home);
        const ancestors = [];
        const seen = new Set([route.key]);
        let parent = routes.get(route.parentKey);
        while (parent && !seen.has(parent.key) && ancestors.length < 6) { ancestors.unshift(parent); seen.add(parent.key); parent = routes.get(parent.parentKey); }
        for (const ancestor of ancestors) { const slash = el('span', null, '/'); slash.setAttribute('aria-hidden', 'true'); const link = el('a', null, 'All ' + ancestor.label); link.href = routeURL(ancestor); fragment.append(slash, link); }
        const slash = el('span', null, '/'); slash.setAttribute('aria-hidden', 'true'); const current = el('span', null, route.label); current.setAttribute('aria-current', 'page'); fragment.append(slash, current); breadcrumbs.replaceChildren(fragment);
      }
      const hero = root.querySelector('[data-k23-hero-image]');
      if (hero && /ii-k23-(?:men-hero|scene-men-studio)(?:-\d+)?\.webp$/i.test(route.sceneAsset || '')) {
        hero.classList.add('ii-k23-page__image-atlas'); hero.width = 1254; hero.height = 1254;
      }
      if (hero && route.sceneAsset && /\.webp$/i.test(route.sceneAsset) && !/^https?:/.test(route.sceneAsset)) {
        hero.srcset = sceneURL(route.sceneAsset, 640) + ' 640w, ' + sceneURL(route.sceneAsset, 960) + ' 960w, ' + sceneURL(route.sceneAsset) + (hero.classList.contains('ii-k23-page__image-atlas') ? ' 1254w' : ' 1536w');
        hero.sizes = '(max-width: 600px) 100vw, 60vw';
      }
      const related = [...new Set(route.relatedKeys || [])].map(relatedKey => routes.get(relatedKey)).filter(Boolean);
      const relatedNodes = [...root.querySelectorAll('[data-k23-related]')];
      if (related.length) {
        for (const tool of tools) {
          let nav = tool.querySelector('[data-k23-related]');
          if (!nav) { nav = el('nav', 'ii-k23-tools__related'); nav.dataset.k23Related = ''; nav.setAttribute('aria-label', 'Related ranges'); tool.append(nav); }
          relatedNodes.push(nav);
        }
        for (const nav of relatedNodes) {
          const fragment = document.createDocumentFragment(); fragment.append(el('span', null, 'Related ranges'));
          for (const target of related) { const link = el('a', null, target.label); link.href = routeURL(target); link.dataset.k23RelatedKey = target.key; fragment.append(link); }
          nav.replaceChildren(fragment); nav.hidden = false;
        }
      }
      // Live Children’s links use their explicit canonical or topic destinations.
    }

    function buildExplore() {
      const tiles = root.querySelector('[data-k23-tiles]');
      const bands = root.querySelector('[data-k23-bands]');
      const children = (route.children || []).map(child => routes.get(child)).filter(Boolean);
      const siblings = route.parentKey ? (routes.get(route.parentKey)?.children || []).map(child => routes.get(child)).filter(Boolean) : [];
      const navigation = children.length ? children : siblings;
      if (tiles && navigation.length) {
        const fragment = document.createDocumentFragment();
        for (const target of navigation) {
          const link = el('a', 'ii-k23-page__tile'); link.href = routeURL(target);
          if (target.key === route.key) link.setAttribute('aria-current', 'page');
          const art = el('span', 'ii-k23-page__tile-art');
          const product = (target.productIds || []).map(id => productMap.get(String(id))).find(item => item && item.image);
          const tileSource = target.image || (product && product.image);
          if (tileSource) { const image = el('img'); image.src = imageURL(tileSource, 320); image.alt = target.label; image.width = 320; image.height = 360; image.loading = 'lazy'; image.decoding = 'async'; art.append(image); }
          else { const image = sceneImage(target); if (image) { image.style.objectFit = 'cover'; art.append(image); } }
          link.append(art, el('strong', null, target.label)); fragment.append(link);
        }
        tiles.replaceChildren(fragment); tiles.hidden = false;
      }
      if (!bands || route.kind !== 'parent') return;
      const fragment = document.createDocumentFragment();
      for (const target of children) {
        const candidates = (target.productIds || []).map(id => productMap.get(String(id))).filter(Boolean);
        const selected = [...candidates.filter(item => item.available), ...candidates.filter(item => !item.available)];
        const band = el('section', 'ii-k23-page__band'); band.dataset.k23Child = target.key; band.id = 'children-range-' + target.key;
        const scene = el('div', 'ii-k23-page__band-scene');
        const image = sceneImage(target.sceneAsset ? target : route); if (image) scene.append(image);
        const copy = el('div', 'ii-k23-page__band-copy'); copy.append(el('p', 'ii-k23-page__eyebrow', target.headline || route.label), el('h2', null, target.label));
        if (target.intro) copy.append(el('p', null, target.intro));
        const link = el('a', 'ii-k23-page__band-link', 'Shop ' + target.label + ' · ' + candidates.length + ' products →'); link.href = routeURL(target); copy.append(link); scene.append(copy); band.append(scene);
        const selection = el('div', 'ii-k23-page__band-products'); selected.forEach(product => selection.append(card(product))); band.append(selection); fragment.append(band);
      }
      bands.replaceChildren(fragment);
    }
    function buildFilters() {
      if (facets) {
        const values = new Map();
        for (const product of products) for (const option of product.options) {
          if (!option.name || (option.name === 'Title' && option.values.every(value => value === 'Default Title'))) continue;
          if (!values.has(option.name)) values.set(option.name, new Set());
          for (const value of option.values) if (typeof value === 'string' && value !== 'Default Title') values.get(option.name).add(value);
        }
        const fragment = document.createDocumentFragment();
        for (const [name, set] of [...values].filter(([, set]) => set.size > 1 && set.size <= 40).slice(0, 8)) {
          const label = el('label', null, name);
          const select = el('select'); select.dataset.k23Option = name;
          const any = el('option', null, 'Any ' + name.toLocaleLowerCase('en-GB')); any.value = ''; select.append(any);
          [...set].sort(collator.compare).forEach(value => { const option = el('option', null, value); option.value = value; select.append(option); });
          select.addEventListener('change', resetAndRender); optionFilters.set(name, select); label.append(select); fragment.append(label);
        }
        facets.replaceChildren(fragment);
      }
      const exactIds = new Set(products.map(product => product.id));
      const explicitGroups = (Array.isArray(route.groups) ? route.groups : []).map(group => ({ key: 'topic:' + group.key, label: group.label, productIds: (group.productIds || []).map(String).filter(id => exactIds.has(id)) }));
      const children = (route.children || []).map(child => routes.get(child)).filter(Boolean).map(target => ({ key: target.key, label: target.label, productIds: (target.productIds || []).map(String).filter(id => exactIds.has(id)) }));
      const definedGroups = [...explicitGroups, ...children].filter((group, index, all) => group.label && group.productIds.length && all.findIndex(other => other.key === group.key) === index);
      if (groupsNode && definedGroups.length > 1) {
        const fragment = document.createDocumentFragment();
        for (const target of [{ key: 'all', label: 'All products' }, ...definedGroups]) {
          const button = el('button', null, target.label); button.type = 'button'; button.dataset.k23Group = target.key; button.setAttribute('aria-pressed', String(target.key === 'all'));
          if (target.key !== 'all') groupMembers.set(target.key, new Set(target.productIds.map(String)));
          button.addEventListener('click', () => { selectedGroup = target.key; groupsNode.querySelectorAll('button').forEach(item => item.setAttribute('aria-pressed', String(item.dataset.k23Group === selectedGroup))); resetAndRender(); });
          fragment.append(button);
        }
        groupsNode.replaceChildren(fragment); groupsNode.hidden = false;
      }
    }
    function matching() {
      const query = search ? search.value.trim().toLocaleLowerCase('en-GB') : '';
      const lower = priceMin && priceMin.value !== '' ? Number(priceMin.value) : null;
      const upper = priceMax && priceMax.value !== '' ? Number(priceMax.value) : null;
      const selected = [...optionFilters].filter(([, control]) => control.value).map(([name, control]) => [name, control.value]);
      const result = products.filter(product => {
        if (query && !product.search.includes(query)) return false;
        if (selectedGroup !== 'all' && !groupMembers.get(selectedGroup)?.has(product.id)) return false;
        return product.variants.some(variant => {
          if (available && available.checked && !variant.available) return false;
          if (lower !== null && (!Number.isFinite(lower) || variant.price < lower)) return false;
          if (upper !== null && (!Number.isFinite(upper) || variant.price > upper)) return false;
          return selected.every(([name, value]) => { const index = product.options.findIndex(option => option.name === name); return index >= 0 && variant.options[index] === value; });
        });
      });
      result.sort((a, b) => {
        switch (sort ? sort.value : 'featured') {
          case 'title-asc': return collator.compare(a.title, b.title) || a.order - b.order;
          case 'title-desc': return collator.compare(b.title, a.title) || a.order - b.order;
          case 'price-asc': return (a.minPrice ?? Infinity) - (b.minPrice ?? Infinity) || a.order - b.order;
          case 'price-desc': return (b.minPrice ?? -Infinity) - (a.minPrice ?? -Infinity) || a.order - b.order;
          default: return a.order - b.order;
        }
      });
      return result;
    }
    function render(focusNew = false) {
      if (!loaded) return;
      const result = matching();
      const shown = result.slice(0, visibleCount);
      const fragment = document.createDocumentFragment(); shown.forEach(product => fragment.append(card(product))); grid.replaceChildren(fragment);
      const badBounds = priceMin && priceMax && priceMin.value !== '' && priceMax.value !== '' && Number(priceMin.value) > Number(priceMax.value);
      status.textContent = badBounds ? 'Minimum price must be lower than maximum price.' : result.length ? shown.length + ' of ' + result.length + (result.length === 1 ? ' product shown.' : ' products shown.') : 'No products match your selection. Try a different title or change the filters.';
      if (more) more.hidden = shown.length >= result.length;
      if (focusNew) grid.children[visibleCount - PAGE_SIZE]?.querySelector('a')?.focus({ preventScroll: true });
    }
    function resetAndRender() { visibleCount = PAGE_SIZE; render(); }
    function setMode(mode) {
      if (mode !== 'explore' && mode !== 'catalogue') return;
      if (pageStatus.mode !== mode) { verificationToken += 1; if (activeDialog) activeDialog.close(); }
      pageStatus.mode = mode;
      allControls.forEach(container => container.querySelectorAll('[data-k23-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.k23Mode === mode))));
      if (presentation === 'catalogue-only') {
        root.hidden = mode !== 'catalogue';
        nativeWrappers.forEach(wrapper => {
          wrapper.hidden = mode === 'catalogue';
          if (mode === 'catalogue') wrapper.style.setProperty('display', 'none', 'important');
          else { const original = nativeDisplay.get(wrapper); if (original.value) wrapper.style.setProperty('display', original.value, original.priority); else wrapper.style.removeProperty('display'); }
        });
      }
      else if (root.dataset.kind === 'parent') { if (explore) explore.hidden = mode === 'catalogue'; if (catalogue) catalogue.hidden = mode !== 'catalogue'; }
      else if (catalogue) catalogue.hidden = false;
      signal();
    }
    function announcement(text, linkLabel = 'View basket', href = localeRoot(root) + 'cart') {
      if (!notice) return;
      notice.replaceChildren(el('p', null, text));
      const link = el('a', null, linkLabel); link.href = href; notice.append(link);
      const close = el('button', 'ii-k23-page__notice-close', '×'); close.type = 'button'; close.setAttribute('aria-label', 'Dismiss basket message'); close.addEventListener('click', () => { notice.hidden = true; }); notice.append(close); notice.hidden = false;
    }
    function unsafeFresh(product, fresh) {
      const expected = Number(product.meta.variantCount);
      if (product.meta.forcePdp || !Number.isInteger(expected) || expected < 1 || expected > 250 || !fresh || String(fresh.id) !== product.id || fresh.handle !== product.handle || !Array.isArray(fresh.variants) || fresh.variants.length !== expected) return true;
      if (fresh.requires_selling_plan || fresh.gift_card || fresh.requires_components || (fresh.selling_plan_groups || []).length) return true;
      if (fresh.variants.some(variant => variant.requires_selling_plan || variant.requires_components || (variant.selling_plan_allocations || []).length)) return true;
      return false;
    }
    async function verify(product) {
      const fresh = await getJSON(localeRoot(root) + 'products/' + encodeURIComponent(product.handle) + '.js');
      if (unsafeFresh(product, fresh)) throw new Error('This product needs its full product page for complete options and configuration.');
      return fresh;
    }
    async function addExact(product, freshVariant, button, dialogStatus) {
      if (button.dataset.k23Busy === 'true') return;
      button.dataset.k23Busy = 'true'; button.disabled = true;
      const original = button.textContent; button.textContent = 'Checking…';
      let postStarted = false;
      const dialogAtStart = activeDialog;
      const tokenAtStart = verificationToken;
      try {
        const latest = await verify(product);
        if (verificationToken !== tokenAtStart || (dialogAtStart && !dialogAtStart.open)) throw new Error('The product selection changed or was closed. Choose the exact option again before adding.');
        const variant = latest.variants.find(item => String(item.id) === String(freshVariant.id));
        if (!variant || !variant.available) throw new Error('This exact option is currently unavailable. Please choose another option or view the product.');
        if (Number(variant.price) !== Number(freshVariant.price) || JSON.stringify(variant.options) !== JSON.stringify(freshVariant.options)) throw new Error('This option has changed. Reopen the options to check its current details before adding.');
        button.textContent = 'Adding…';
        const aborter = new AbortController();
        const timer = window.setTimeout(() => aborter.abort(), 20000);
        let response;
        let payload;
        try {
          postStarted = true;
          response = await fetch(localeRoot(root) + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: String(variant.id), quantity: 1 }] }), signal: aborter.signal });
          payload = await response.json();
        } finally { window.clearTimeout(timer); }
        if (!response.ok || payload.status || payload.errors) throw new Error(typeof payload.description === 'string' ? payload.description : 'The basket could not be confirmed.');
        const added = Array.isArray(payload.items) ? payload.items : [payload];
        if (!added.some(item => String(item.variant_id || item.id) === String(variant.id))) throw new Error('The basket response did not confirm this exact option.');
        const title = latest.title + (variant.title && variant.title !== 'Default Title' ? ' — ' + variant.title : '');
        if (activeDialog) activeDialog.close();
        announcement('Added to basket: ' + title + '.');
        button.textContent = 'Added';
        button.disabled = false;
      } catch (error) {
        const message = postStarted ? error.message + ' Your basket may have changed. Check it before trying again.' : error.message;
        if (dialogStatus) dialogStatus.textContent = message;
        announcement(message, postStarted ? 'Check basket' : 'View product', postStarted ? localeRoot(root) + 'cart' : pdpURL(product, freshVariant.id));
        button.textContent = postStarted ? 'Check basket' : original;
        button.disabled = postStarted;
        if (postStarted) button.dataset.k23Uncertain = 'true';
      } finally {
        button.dataset.k23Busy = 'false';
        if (!postStarted || button.dataset.k23Uncertain !== 'true') button.disabled = false;
      }
    }

    function openDialog(product, fresh, opener) {
      if (typeof HTMLDialogElement === 'undefined') { announcement('Please use the product page to choose these options.', 'View product', pdpURL(product)); return; }
      if (activeDialog) activeDialog.close();
      const dialog = el('dialog', 'ii-k23-page__dialog');
      const headingId = 'ii-k23-option-heading-' + (++modalSequence);
      dialog.setAttribute('aria-labelledby', headingId);
      const head = el('div', 'ii-k23-page__dialog-head');
      const heading = el('h2', null, fresh.title); heading.id = headingId;
      const close = el('button', 'ii-k23-page__dialog-close', '×'); close.type = 'button'; close.setAttribute('aria-label', 'Close product options'); close.addEventListener('click', () => dialog.close()); head.append(heading, close); dialog.append(head);
      const body = el('div', 'ii-k23-page__dialog-body');
      const image = el('img', 'ii-k23-page__dialog-image');
      const initialImage = imageURL(fresh.featured_image || product.image);
      if (initialImage) image.src = initialImage; else image.hidden = true;
      image.alt = ''; image.width = 560; image.height = 650; body.append(image);
      const controls = el('div', 'ii-k23-page__dialog-options');
      const price = el('p', 'ii-k23-page__dialog-price', 'Choose your options'); controls.append(price);
      const detail = el('p', 'ii-k23-page__dialog-status', 'Select each option to see the exact price and availability.'); detail.setAttribute('role', 'status'); detail.setAttribute('aria-live', 'polite');
      const native = el('a', 'ii-k23-page__dialog-pdp', 'View full product details'); native.href = pdpURL(product);
      const add = el('button', 'ii-k23-page__button', 'Add to basket'); add.type = 'button'; add.disabled = true;
      const options = (fresh.options || []).map((option, index) => typeof option === 'string' ? { name: option, position: index + 1 } : option);
      const selectors = [];
      let selectedVariant = fresh.variants.length === 1 ? fresh.variants[0] : null;
      if (fresh.variants.length > 1) for (let index = 0; index < options.length; index += 1) {
        const name = options[index].name === 'Title' ? 'Option' : options[index].name;
        const label = el('label', null, name);
        const select = el('select'); select.dataset.k23VariantOption = String(index); select.setAttribute('aria-label', name);
        const blank = el('option', null, 'Select ' + name.toLocaleLowerCase('en-GB')); blank.value = ''; select.append(blank);
        [...new Set(fresh.variants.map(variant => variant.options[index]).filter(value => typeof value === 'string'))].forEach(value => { const option = el('option', null, value); option.value = value; select.append(option); });
        select.addEventListener('change', () => {
          // No automatic variant choice: an upstream change clears later choices explicitly.
          selectors.slice(index + 1).forEach(control => { control.value = ''; });
          update();
        });
        selectors.push(select); label.append(select); controls.append(label);
      }
      function update() {
        selectors.forEach((select, index) => {
          for (const option of select.options) {
            if (!option.value) continue;
            option.disabled = !fresh.variants.some(variant => variant.available && variant.options[index] === option.value && selectors.slice(0, index).every((previous, position) => !previous.value || variant.options[position] === previous.value));
          }
        });
        const complete = selectors.every(select => Boolean(select.value));
        const exact = complete ? fresh.variants.filter(variant => selectors.every((select, index) => variant.options[index] === select.value)) : [];
        selectedVariant = fresh.variants.length === 1 ? fresh.variants[0] : exact.length === 1 ? exact[0] : null;
        if (selectedVariant) {
          price.textContent = money(Number(selectedVariant.price), true);
          detail.textContent = selectedVariant.available ? 'This exact option is available to add.' : 'This exact option is currently sold out.';
          native.href = pdpURL(product, selectedVariant.id);
          const selectedImage = selectedVariant.featured_image && (selectedVariant.featured_image.src || selectedVariant.featured_image);
          const currentImage = imageURL(selectedImage || fresh.featured_image || product.image);
          if (currentImage) { image.src = currentImage; image.hidden = false; } else image.hidden = true;
        } else {
          price.textContent = 'Choose your options';
          detail.textContent = complete ? 'This combination cannot be selected here. Please use the full product page.' : 'Select each option. Changing an earlier option clears the choices after it.';
          native.href = pdpURL(product);
        }
        add.disabled = !selectedVariant || !selectedVariant.available;
      }
      add.addEventListener('click', () => { if (selectedVariant && selectedVariant.available) addExact(product, selectedVariant, add, detail); });
      controls.append(detail, add, native); body.append(controls); dialog.append(body); root.append(dialog);
      dialog.addEventListener('close', () => { verificationToken += 1; if (activeDialog === dialog) activeDialog = null; dialog.remove(); opener.focus({ preventScroll: true }); });
      dialog.addEventListener('click', event => { if (event.target === dialog) { const box = dialog.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close(); } });
      activeDialog = dialog; update(); dialog.showModal();
    }

    root.addEventListener('click', async event => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const openAll = target.closest('[data-k23-open-catalogue]');
      if (openAll) { event.preventDefault(); setMode('catalogue'); catalogue.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' }); }
      const button = target.closest('[data-k23-quick]');
      if (!button || button.disabled) return;
      const product = productMap.get(button.dataset.k23Quick);
      if (!product) return;
      const token = ++verificationToken;
      const original = button.textContent; button.disabled = true; button.textContent = 'Checking…';
      try {
        const fresh = await verify(product);
        if (token !== verificationToken) return;
        if (fresh.variants.length === 1 && Number(product.meta.variantCount) === 1) await addExact(product, fresh.variants[0], button);
        else openDialog(product, fresh, button);
      } catch (error) { if (token === verificationToken) announcement(error.message, 'View product', pdpURL(product)); }
      finally { if (button.dataset.k23Uncertain !== 'true') { button.disabled = false; button.textContent = original; } }
    });
    allControls.forEach(container => container.querySelectorAll('[data-k23-mode]').forEach(button => button.addEventListener('click', () => setMode(button.dataset.k23Mode))));
    if (search) search.addEventListener('input', () => { window.clearTimeout(searchTimer); searchTimer = window.setTimeout(resetAndRender, 180); });
    for (const control of [sort, available, priceMin, priceMax]) if (control) control.addEventListener('change', resetAndRender);
    if (more) more.addEventListener('click', () => { visibleCount += PAGE_SIZE; render(true); });
    const retry = root.querySelector('[data-k23-retry]'); if (retry) retry.addEventListener('click', () => { clearPublicCache(data); load(); });

    async function load() {
      if (loaded || loading) return;
      loading = true;
      if (errorNode) errorNode.hidden = true;
      if (retry) retry.disabled = true;
      grid.setAttribute('aria-busy', 'true');
      status.textContent = 'Loading the current selection…';
      pageStatus.ready = false; pageStatus.errors = []; signal();
      try {
        data = await loadData(root.dataset.feedUrl);
        if (!data || !Array.isArray(data.routes) || !Array.isArray(data.products)) throw new Error('The collection data could not be verified.');
        routes = new Map(data.routes.map(item => [item.key, item]));
        route = routes.get(key);
        if (!route || !Array.isArray(route.productIds)) throw new Error('This collection could not be loaded.');
        metadata = new Map(data.products.map(product => [String(product.id), product]));
        const configured = [...new Set(route.productIds.map(String))];
        pageStatus.expectedCount = configured.length;
        decorate();
        const source = await publicProducts(root, data);
        const facts = new Map(source.products.map(product => [product.id, product]));
        const missing = configured.filter(id => !facts.has(id));
        pageStatus.feedPages = source.pages; pageStatus.cached = source.cached; pageStatus.missingIds = missing; pageStatus.loadedCount = configured.length - missing.length;
        // A complete public feed is authoritative for current publication. A withdrawn
        // product must not hide the remaining published range. Failed/partial feeds
        // still throw above; absent products never receive a card or basket action.
        pageStatus.unpublishedIds = missing;
        productMap = new Map(source.products.map((fact, index) => [fact.id, normalise(fact, index)]));
        products = configured.filter(id => productMap.has(id)).map((id, index) => ({ ...productMap.get(id), order: index }));
        for (const product of products) productMap.set(product.id, product);
        buildExplore(); buildFilters(); loaded = true;
        [search, sort, available, priceMin, priceMax].forEach(control => { if (control) control.disabled = false; });
        if (fallback) fallback.hidden = true;
        grid.removeAttribute('aria-busy');
        pageStatus.ready = true;
        render(); signal();
      } catch (error) {
        pageStatus.errors = [error.message]; status.textContent = 'The complete selection could not be verified.';
        if (errorText) errorText.textContent = error.message + ' Use the current collection below or try again.';
        if (errorNode) errorNode.hidden = false;
        if (fallback) fallback.hidden = false;
        signal();
      } finally { loading = false; grid.removeAttribute('aria-busy'); if (retry) retry.disabled = false; }
    }
    setMode(presentation === 'catalogue-only' || root.dataset.kind === 'parent' ? 'explore' : 'catalogue');
    load();
  }
  function start() { document.querySelectorAll('[data-k23-page]').forEach(initialise); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
